import { Colors, Icon, Text } from '@mercator-gcp/grout';
import { FlexRow } from 'components/layout/FlexRow';
import { useCrmPushStatus, usePushToCrm } from 'hooks/useCrmPush';
import { useActiveCrmConnection } from 'hooks/useOneAuthConnections';
import { useToast } from 'hooks/useToast';
import { CrmResourceType } from 'services/API/OneAuthAPI';

const listItemContentStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  borderRadius: '4px',
  gap: 'padding_8',
  _hover: {
    backgroundColor: Colors.ArmyGreen100,
  },
  width: '100%',
  padding: 'padding_8',
};

type Props = {
  resourceType: CrmResourceType;
  resourceId: string;
};

// Menu action to push the current project/document into the user's connected
// CRM. Renders nothing until the user has connected a CRM.
const PushToCrm = ({ resourceType, resourceId }: Props) => {
  const { renderToast } = useToast();
  const { connection } = useActiveCrmConnection();

  const { data: pushStatus } = useCrmPushStatus(
    resourceType,
    resourceId,
    Boolean(connection),
  );
  const pushToCrm = usePushToCrm();

  if (!connection) {
    return null;
  }

  const platformLabel = connection.title || connection.platform;
  const alreadyPushed = Boolean(pushStatus?.pushed);

  const handlePush = () => {
    if (pushToCrm.isLoading) return;
    pushToCrm.mutate(
      { resourceType, resourceId },
      {
        onSuccess: () =>
          renderToast({
            toastTitle: alreadyPushed
              ? `Updated in ${platformLabel}`
              : `Added to ${platformLabel}`,
            status: 'success',
            title: 'Success',
            label: `This ${resourceType} was sent to ${platformLabel}.`,
          }),
        onError: () =>
          renderToast({
            toastTitle: `Failed to push to ${platformLabel}`,
            status: 'error',
            title: 'Error',
            label: 'Please try again.',
          }),
      },
    );
  };

  if (alreadyPushed) {
    return (
      <FlexRow onClick={handlePush} cursor="pointer" sx={listItemContentStyle}>
        <Icon name={Icon.Name.RefreshArrowsCycle} fill={Colors.ForestGreen900} />
        <Text.BodyBold>Update in {platformLabel}</Text.BodyBold>
      </FlexRow>
    );
  }

  return (
    <FlexRow onClick={handlePush} cursor="pointer" sx={listItemContentStyle}>
      <Icon name={Icon.Name.SendArrow} fill={Colors.ForestGreen900} />
      <Text.BodyBold>Push to {platformLabel}</Text.BodyBold>
    </FlexRow>
  );
};

export default PushToCrm;
